import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import type { CommunityMemberPaymentMethod } from "@/react/types/CommunityMemberPaymentMethod";
import type { Paywall } from "@/react/types/Paywall";
import type { PaywallCoupon } from "@/react/types/PaywallCoupon";
import type { PaywallPrice } from "@/react/types/PaywallPrice";
import { usePunditUserContext } from "@circle-react/contexts";

const CreatePaywallSubscriptionFormContext =
  createContext<CreatePaywallSubscriptionFormContextProp>({
    currentPaymentMethod: null,
    setCurrentPaymentMethod: () => {},
  });
CreatePaywallSubscriptionFormContext.displayName =
  "CreatePaywallSubscriptionFormContext";

interface CreatePaywallSubscriptionFormContextProp {
  communityId?: number;
  communityMemberId?: string;
  couponsIndex?: Record<string, PaywallCoupon>;
  currentPaymentMethod: CommunityMemberPaymentMethod | null;
  paywallPricesIndex?: Record<string, PaywallPrice>;
  paywallsIndex?: Record<string, Paywall>;
  setCouponsIndex?: (object: Record<string, PaywallCoupon>) => void;
  setCurrentPaymentMethod: (
    paymentMethod: CommunityMemberPaymentMethod | null,
  ) => void;
  setPaywallPricesIndex?: (object: Record<string, PaywallPrice>) => void;
  setPaywallsIndex?: (object: Record<string, Paywall>) => void;
}

export interface CreatePaywallSubscriptionFormContextProviderProps {
  children: ReactNode;
  communityMemberId?: string;
}

export const CreatePaywallSubscriptionFormContextProvider = ({
  communityMemberId,
  children,
}: CreatePaywallSubscriptionFormContextProviderProps) => {
  const [paywallsIndex, setPaywallsIndex] = useState({});
  const [paywallPricesIndex, setPaywallPricesIndex] = useState({});
  const [couponsIndex, setCouponsIndex] = useState({});
  const [currentPaymentMethod, setCurrentPaymentMethod] =
    useState<CommunityMemberPaymentMethod | null>(null);
  const { currentCommunity } = usePunditUserContext();

  const valueToStore = {
    communityId: currentCommunity?.id,
    communityMemberId,
    paywallsIndex,
    setPaywallsIndex,
    paywallPricesIndex,
    setPaywallPricesIndex,
    couponsIndex,
    setCouponsIndex,
    currentPaymentMethod,
    setCurrentPaymentMethod,
  };

  return (
    <CreatePaywallSubscriptionFormContext.Provider value={valueToStore}>
      {children}
    </CreatePaywallSubscriptionFormContext.Provider>
  );
};

export const useCreatePaywallSubscriptionFormContext = () =>
  useContext<CreatePaywallSubscriptionFormContextProp>(
    CreatePaywallSubscriptionFormContext,
  );
